/**
 * src/views/focus/TaskEdit.jsx — Task creation/edit modal
 * Extracted from FocusView.jsx
 *
 * Renamed single-letter variables:
 *   P  → PRIORITIES
 *   ST → STATUSES
 *   f/sF → formData/setFormData
 *   s  → setField
 *   ed → isEditing
 *   td → today
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { ModalSheet } from '../../components/SharedUI';
import Button from '../../components/shared/Button';
import { useT } from '../../lib/i18n/I18nContext';

const PRIORITIES = {
  P1: { c: "var(--p1)", b: "var(--p1b)", r: "var(--bad)" },
  P2: { c: "var(--p2)", b: "var(--p2b)", r: "var(--warn)" },
  P3: { c: "var(--p3)", b: "var(--p3b)", r: "var(--ok)" },
};

const STATUSES = {
  "To Do": { c: "var(--stodo)", b: "var(--stodob)" },
  "In Progress": { c: "var(--spro)", b: "var(--sprob)" },
  "Blocked": { c: "var(--sblk)", b: "var(--sblkb)" },
  "Done": { c: "var(--sdn)", b: "var(--sdnb)" },
};

const AREAS = ["All", "Procurement", "Production", "Installation", "Admin", "HR", "Finances", "General"];

const generateId = () => Math.random().toString(36).slice(2, 9);
const today = () => new Date().toISOString().slice(0, 10);

export default function TaskEdit({ task, onSave, onClose, onDel, projects = [], people = [], peopleLookup = {}, bundles = [] }) {
  const { t } = useT();
  const [formData, setFormData] = useState(
    task || { title: "", pr: "P2", st: "To Do", area: "Admin", due: "", notes: "", who: "", whoId: "", projectId: "", bundleId: "" }
  );
  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const isEditing = !!task?.id;
  const projectBundles = formData.projectId
    ? bundles.filter(b => b.project_id === formData.projectId)
    : [];
  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    border: "1px solid var(--bdr)", background: "var(--card)",
    fontSize: 13, color: "var(--t1)", outline: "none", minHeight: 44,
  };

  return (
    <ModalSheet open={true} onClose={onClose} title={isEditing ? "Edit Task" : "New Task"}>
      <textarea autoFocus value={formData.title} onChange={e => setField("title", e.target.value)}
        placeholder={t('addTaskForm.whatNeedsToHappen')} rows={2}
        style={{
          width: "100%", border: "none", outline: "none", fontSize: 17,
          fontWeight: 500, color: "var(--t1)", background: "transparent",
          resize: "none", lineHeight: 1.4, marginBottom: 16, padding: 0,
        }} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Priority</div>
        <div style={{ display: "flex", gap: 8 }}>
          {Object.entries(PRIORITIES).map(([key, colors]) => (
            <button key={key} onClick={() => setField("pr", key)} style={{
              padding: "10px 16px", borderRadius: 99, fontSize: 12, fontWeight: 700, minHeight: 44,
              background: formData.pr === key ? colors.b : "var(--card)",
              color: formData.pr === key ? colors.c : "var(--tm)",
              outline: formData.pr === key ? `2px solid ${colors.r}` : "1px solid var(--bdr)",
              border: "none", cursor: "pointer",
            }}>{key}</button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Status</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {"To Do,In Progress,Blocked,Done".split(",").map(status => {
            const colors = STATUSES[status];
            const isActive = formData.st === status;
            return (
              <button key={status} onClick={() => setField("st", status)} style={{
                padding: "10px 14px", borderRadius: 99, fontSize: 12, fontWeight: 500, minHeight: 44,
                background: isActive ? colors.b : "var(--card)",
                color: isActive ? colors.c : "var(--tm)",
                outline: isActive ? "2px solid" : "1px solid var(--bdr)",
                outlineColor: isActive ? colors.c : undefined,
                border: "none", cursor: "pointer",
              }}>{status}</button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Assign to</div>
          <select value={formData.whoId || ""} onChange={e => {
            const personId = e.target.value;
            const personName = personId ? (peopleLookup[personId] || '') : '';
            setFormData(prev => ({ ...prev, whoId: personId, who: personName }));
          }} style={inputStyle}>
            <option value="">Unassigned</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.display_name || p.id}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Area</div>
          <select value={formData.area} onChange={e => setField("area", e.target.value)} style={inputStyle}>
            {AREAS.slice(1).map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {projects.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Project</div>
          <select value={formData.projectId || ""} onChange={e => {
            setField("projectId", e.target.value);
            setField("bundleId", ""); // reset bundle when project changes
          }} style={inputStyle}>
            <option value="">No project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name || p.id}</option>)}
          </select>
        </div>
      )}

      {projectBundles.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Bundle</div>
          <select value={formData.bundleId || ""} onChange={e => setField("bundleId", e.target.value)} style={inputStyle}>
            <option value="">No bundle</option>
            {projectBundles.map(b => <option key={b.id} value={b.id}>{b.title || b.name || b.id}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Due</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="date" value={formData.due} onChange={e => setField("due", e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            <button onClick={() => setField("due", today())} style={{
              padding: "10px 12px", borderRadius: 8, background: "var(--glass)",
              border: "none", color: "var(--accent)", fontSize: 11,
              fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", minHeight: 44,
            }}>Today</button>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Budget Hours</div>
          <input type="number" step="0.5" min="0" value={formData.budgetHours || ""} onChange={e => setField("budgetHours", e.target.value)} placeholder="e.g. 8" style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--t2)", marginBottom: 6 }}>Notes</div>
        <textarea value={formData.notes} onChange={e => setField("notes", e.target.value)} placeholder="Context..." rows={2} style={{ ...inputStyle, resize: "none" }} />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="primary"
          onClick={() => formData.title.trim() && onSave({
            ...formData,
            id: task?.id || generateId(),
            cr: task?.cr || Date.now(),
            _airtable: task?._airtable || false,
          })}
          disabled={!formData.title.trim()}
          data-testid="task-edit-save"
          style={{ flex: 1, padding: 12, borderRadius: 14, fontSize: 15, minHeight: 48 }}
        >{isEditing ? "Save" : "Add Task"}</Button>
        {isEditing && (
          <button onClick={() => onDel(task.id)} style={{
            padding: "12px 16px", borderRadius: 14,
            background: "var(--p1b)", color: "var(--bad)",
            minHeight: 48, display: "flex", alignItems: "center",
            border: "none", cursor: "pointer",
          }}><Trash2 size={20} /></button>
        )}
      </div>
    </ModalSheet>
  );
}
